/**
 * Left-side timeline panel for in-game history.
 * Layout similar to Chat but on the left with amber color scheme; uses full height below the top bar
 * (no large top gap) since zoom controls are only on the right.
 * Uses spring animations for expand/collapse, clickable events, and step controls.
 */

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CounterClockwiseClockIcon,
  Cross1Icon,
  PlayIcon,
} from '@radix-ui/react-icons';
import { animated, useSpring } from '@react-spring/web';
import { debounce } from 'lodash';
import { Tooltip } from 'radix-ui';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  selectEventLog,
  selectHistoryCursor,
  selectLatestGameStateAnchor,
  selectLeftPanelOpen,
  selectPlayers,
  selectSelectedStep,
  useSelector,
  useTranslation,
} from '~/app';
import { getNextAnimationSequenceId } from '~/board';
import { setCurrentState, triggerRejoinGame } from '~/game/actions';
import calculateClientHorseColors from '~/game/calculateClientHorseColors';
import { getReplaySyncActions, type ReplaySyncPathOptions } from '~/game/sagaGameReceiver';
import {
  type HistoryCursor,
  setCardDice,
  setHistoryCursor,
  setHistoryPanelState,
  setLastCard,
  setLastCardHorseIndex,
  setLastTurnDicePlayerId,
  setLastTurnDices,
  setLastTurnDicesCur,
} from '~/overlay/actions';
import {
  BAR_BOTTOM_HEIGHT_PERCENT,
  BAR_TOP_HEIGHT_PERCENT,
  FAB_BOTTOM_INSET_REM,
  FAB_EDGE_INSET_REM,
  PANEL_BOTTOM_INSET_REM,
  PANEL_TOP_INSET_LEFT_REM,
  PANEL_TOP_INSET_LEFT_SPECTATOR_REM,
  RESULTS_LAYOUT_FAB_VERTICAL_CLASS,
  RESULTS_LAYOUT_PANEL_VERTICAL,
} from '~/overlay/overlaySpacing';
import { loadPanelExpanded, savePanelExpanded } from '~/overlay/panelPreferencesStorage';
import { buildSemanticSteps } from '~/replay/eventCompression';
import type { GameStateAtEvent } from '~/replay/reducers';
import { computeStateAtEventIndex } from '~/replay/reducers';
import { getReplayOverlayDisplay } from '~/replay/replayOverlay';
import Turfmaster, { Board as BoardModel } from '~/server/src/tmmodel';
import { cn } from '~/utils/styles';

import { type HistorySelectionIntent, HistoryTimeline } from './HistoryTimeline';
import { resolveCursorSelection } from './resolveCursorSelection';

export type HistoryPanelInteractionMode = 'interactive' | 'readOnly';
export type HistoryPanelLayout = 'board' | 'results';

export interface HistoryPanelProps {
  onClose: () => void;
  /** Initial event index when entering history mode */
  initialEventIndex?: number;
  /** If true, history mode is active (show panel), if false show button */
  isHistoryMode?: boolean;
  /** If true, spectator LIVE pill is shown; panel uses larger top inset to avoid overlap */
  isSpectatorMode?: boolean;
  /** Callback to toggle history mode (called when button is clicked) */
  onToggle?: () => void;
  /** Callback to exit history mode (called when stop button is clicked) */
  onExitHistoryMode?: () => void;
  /**
   * `readOnly`: timeline selection is local only (no replay sync, no Redux history cursor).
   * Used on the post-game results screen.
   */
  interactionMode?: HistoryPanelInteractionMode;
  /** `results`: viewport-fixed, full-height insets (no zoom-row gap). Default matches in-game overlay. */
  layout?: HistoryPanelLayout;
}

const HistoryPanel: React.FC<HistoryPanelProps> = ({
  onClose: _onClose,
  isHistoryMode = false,
  isSpectatorMode = false,
  onToggle,
  onExitHistoryMode,
  interactionMode = 'interactive',
  layout = 'board',
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isReadOnly = interactionMode === 'readOnly';
  const leftPanelOpen = useSelector(selectLeftPanelOpen);
  const eventLog = useSelector(selectEventLog);
  const anchorState = useSelector(selectLatestGameStateAnchor);
  const players = useSelector(selectPlayers);
  const historyCursorFromStore = useSelector(selectHistoryCursor);
  const [readOnlyCursor, setReadOnlyCursor] = useState<HistoryCursor>({ kind: 'live' });
  const historyCursor = isReadOnly ? readOnlyCursor : historyCursorFromStore;
  const selectedStep = useSelector(selectSelectedStep);
  const [isExpanded, setIsExpanded] = useState(() => loadPanelExpanded('history') ?? false);
  // Show panel when history mode is active, button when inactive.
  // Use controlled visibility when parent provides onToggle, otherwise keep local visibility state.
  const [isVisibleInternal, setIsVisibleInternal] = useState(isHistoryMode);
  const isVisible = onToggle ? isHistoryMode : isVisibleInternal;

  // Dispatch panel state changes to Redux
  useEffect(() => {
    dispatch(setHistoryPanelState({ isVisible, isExpanded }));
  }, [isVisible, isExpanded, dispatch]);

  useEffect(() => {
    savePanelExpanded('history', isExpanded);
  }, [isExpanded]);

  // Cleanup flush timeout when panel becomes invisible
  useEffect(() => {
    if (!isVisible) {
      if (navigationFlushTimeoutRef.current) {
        clearTimeout(navigationFlushTimeoutRef.current);
        navigationFlushTimeoutRef.current = null;
      }
    }
  }, [isVisible]);

  const panelRef = useRef<HTMLDivElement>(null);
  // Track previous selectedStep to detect history->live transition (avoids multiple dispatches / bot races).
  const prevSelectedStepRef = useRef<number | null>(selectedStep);
  /** Last event index we synced to (for replay path range: from -> to). */
  const lastSyncedEventIndexRef = useRef<number>(0);
  const isKeyboardNavRef = useRef(false);
  const lastKeyPressTimeRef = useRef<number>(0);
  const navigationFlushTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Replace direct window.innerWidth usage with safe default
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Build semantic steps (compressed event list)
  const semanticSteps = useMemo(() => buildSemanticSteps(eventLog), [eventLog]);

  // Derived from cursor: live vs semantic (latest-history snapshot is semantic at last step)
  const isLive = historyCursor.kind === 'live';

  // Compute the event index for state derivation (immediate for UI)
  const stateEventIndexImmediate = useMemo(() => {
    if (historyCursor.kind === 'live') return eventLog.length;
    const step = semanticSteps.find((s) => s.originalIndex === historyCursor.originalIndex);
    return step ? step.targetEventIndex : eventLog.length;
  }, [historyCursor, semanticSteps, eventLog.length]);

  // Map cursor to timeline selectedEventIndex (state-after-event index)
  const selectedEventIndex = useMemo(() => {
    if (historyCursor.kind === 'live') return eventLog.length;
    const step = semanticSteps.find((s) => s.originalIndex === historyCursor.originalIndex);
    return step ? step.targetEventIndex : eventLog.length;
  }, [historyCursor, semanticSteps, eventLog.length]);

  const onSelectedEventIndexChange = useCallback(
    (index: number | null, selectionIntent?: HistorySelectionIntent) => {
      const nextCursor = resolveCursorSelection({
        selectedIndex: index,
        eventLogLength: eventLog.length,
        semanticSteps,
        selectionIntent,
      });
      if (!nextCursor) return;
      if (isReadOnly) {
        setReadOnlyCursor(nextCursor);
      } else {
        dispatch(setHistoryCursor(nextCursor));
      }
    },
    [eventLog.length, semanticSteps, dispatch, isReadOnly],
  );

  // Debounced version of stateEventIndex for expensive state computation
  // When user spams arrow keys, we only compute state after they stop navigating
  // Live mode (selectedStep === null) is always immediate - no debounce
  const [debouncedStateEventIndex, setDebouncedStateEventIndex] =
    useState(stateEventIndexImmediate);

  // Create debounced setter function using lodash.debounce
  const debouncedSetStateEventIndex = useMemo(
    () =>
      debounce((value: number) => {
        setDebouncedStateEventIndex(value);
      }, 150), // 150ms debounce - short enough to feel responsive, long enough to batch rapid navigation
    [],
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSetStateEventIndex.cancel();
    };
  }, [debouncedSetStateEventIndex]);

  useEffect(() => {
    if (isReadOnly) return;
    // Live mode should be immediate - no debounce
    if (isLive) {
      debouncedSetStateEventIndex.cancel(); // Cancel any pending debounced calls
      return;
    }

    // Use debounced function for history mode
    debouncedSetStateEventIndex(stateEventIndexImmediate);
  }, [stateEventIndexImmediate, isLive, debouncedSetStateEventIndex, isReadOnly]);

  // Use debounced stateEventIndex for expensive computation
  const effectiveStateEventIndex = isLive ? stateEventIndexImmediate : debouncedStateEventIndex;
  const derivedState = useMemo(() => {
    if (isReadOnly || !anchorState) return null;
    return computeStateAtEventIndex(anchorState, eventLog, effectiveStateEventIndex);
  }, [anchorState, eventLog, effectiveStateEventIndex, isReadOnly]);

  // Helper: sync a computed state to Redux (optionally with replay path range for history animation)
  const syncStateToRedux = useCallback(
    (
      state: ReturnType<typeof computeStateAtEventIndex>,
      pathOptions?: {
        fromEventIndex: number;
        toEventIndex: number;
      },
    ) => {
      if (!anchorState) return;
      const rawBoardId =
        (anchorState as Record<string, unknown>).boardid ??
        (anchorState as Record<string, unknown>).boardId;
      const boardId = typeof rawBoardId === 'number' ? rawBoardId : 0;
      const board = new BoardModel(boardId);
      const clientHorseColors = calculateClientHorseColors(
        state.horses ?? [],
        state.players ?? [],
        0,
      );
      const pathOpts: ReplaySyncPathOptions | undefined =
        pathOptions && eventLog.length > 0
          ? {
              eventLog,
              anchorState: anchorState as GameStateAtEvent,
              fromEventIndex: pathOptions.fromEventIndex,
              toEventIndex: pathOptions.toEventIndex,
              sequenceId: getNextAnimationSequenceId(),
            }
          : undefined;
      const actions = getReplaySyncActions(state, board, clientHorseColors, pathOpts);
      dispatch(setCurrentState(state.currentState ?? Turfmaster.stateMachine.NEWGAME));
      actions.forEach((a) => dispatch(a));
    },
    [anchorState, dispatch, eventLog],
  );

  // Single sync effect: runs on navigation and when debounced index settles
  // Still does NOT fire directly on eventLog/anchor changes during history view
  useEffect(() => {
    if (isReadOnly) return;
    if (selectedStep === null) {
      // Live mode: only sync when transitioning from history (non-null) to live.
      // This prevents replay sync from continuously overwriting live saga move paths.
      // Without this guard, syncStateToRedux overwrites live horse move paths with single-point positions.
      const wasInHistory = prevSelectedStepRef.current !== null;
      if (anchorState && wasInHistory) {
        const liveState = computeStateAtEventIndex(anchorState, eventLog, eventLog.length);
        // Pass path options for history->live transition so path animation can run
        const fromEventIndex = lastSyncedEventIndexRef.current;
        const toEventIndex = eventLog.length;
        syncStateToRedux(liveState, { fromEventIndex, toEventIndex });
        lastSyncedEventIndexRef.current = toEventIndex;
        dispatch(triggerRejoinGame());
      }
      prevSelectedStepRef.current = null;
    } else {
      // Check if we're transitioning from live mode BEFORE updating prevSelectedStepRef
      const wasInLiveMode = prevSelectedStepRef.current === null;
      prevSelectedStepRef.current = selectedStep;
      // History mode: sync the derived historical state to Redux and overlay display
      // Only sync when the debounced state index has caught up to the immediate index.
      // Otherwise we can end up syncing a stale derivedState and never re-run for the final selection.
      if (derivedState && debouncedStateEventIndex === stateEventIndexImmediate) {
        // When navigating backward from live mode, lastSyncedEventIndexRef.current might be stale.
        // Use the current event index (eventLog.length) as fromEventIndex if we're going backward
        // and lastSyncedEventIndexRef.current is smaller than the target.
        const toEventIndex = debouncedStateEventIndex;
        const currentEventIndex = eventLog.length;
        const lastSyncedIndex = lastSyncedEventIndexRef.current;
        // If transitioning from live mode to history mode (wasInLiveMode), use the current event index
        // as fromEventIndex instead of the stale lastSyncedEventIndexRef, regardless of direction.
        // This ensures backward navigation starts from the correct current position.
        const fromEventIndex = wasInLiveMode ? currentEventIndex : lastSyncedIndex;
        syncStateToRedux(derivedState, { fromEventIndex, toEventIndex });
        lastSyncedEventIndexRef.current = toEventIndex;
        const overlayDisplay = getReplayOverlayDisplay(
          anchorState as GameStateAtEvent,
          eventLog,
          debouncedStateEventIndex,
          derivedState as GameStateAtEvent,
        );
        dispatch(setLastTurnDices((derivedState.turndices as number[]) ?? []));
        dispatch(setLastTurnDicesCur((derivedState.turndicescur as number) ?? -1));
        dispatch(setLastTurnDicePlayerId((derivedState.turndiceplayerid as number) ?? -1));
        dispatch(setLastCard(overlayDisplay.lastCard));
        dispatch(setLastCardHorseIndex(overlayDisplay.lastCardHorseIndex));
        dispatch(setCardDice(overlayDisplay.lastCard));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStep, debouncedStateEventIndex, stateEventIndexImmediate, isReadOnly]);

  const handleStepBack = useCallback(() => {
    if (semanticSteps.length === 0) return;

    if (historyCursor.kind === 'live') {
      const lastStep = semanticSteps[semanticSteps.length - 1];
      dispatch(setHistoryCursor({ kind: 'semantic', originalIndex: lastStep.originalIndex }));
    } else {
      const idx = semanticSteps.findIndex((s) => s.originalIndex === historyCursor.originalIndex);
      if (idx > 0) {
        dispatch(
          setHistoryCursor({
            kind: 'semantic',
            originalIndex: semanticSteps[idx - 1].originalIndex,
          }),
        );
      }
    }
  }, [historyCursor, semanticSteps, dispatch]);

  const handleStepForward = useCallback(() => {
    if (historyCursor.kind === 'live') return;

    const idx = semanticSteps.findIndex((s) => s.originalIndex === historyCursor.originalIndex);
    if (idx < semanticSteps.length - 1) {
      dispatch(
        setHistoryCursor({ kind: 'semantic', originalIndex: semanticSteps[idx + 1].originalIndex }),
      );
    } else {
      dispatch(setHistoryCursor({ kind: 'live' }));
    }
  }, [historyCursor, semanticSteps, dispatch]);

  // Keyboard navigation when panel is visible or in history mode (arrow keys work with panel closed)
  useEffect(() => {
    if (isReadOnly) return;
    if (!isVisible && isLive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle keys when not typing in an input/textarea
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Check if focus is within the panel (only relevant when panel is open)
      const activeElement = document.activeElement;
      const isFocusInPanel = panelRef.current?.contains(activeElement);

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        event.stopPropagation();
        isKeyboardNavRef.current = true;
        // Blur active element when panel is open to prevent focus ring from moving, which causes laggy scrolling
        if (isVisible && isFocusInPanel && activeElement instanceof HTMLElement) {
          activeElement.blur();
        }
        // Track keypress time for flush mechanism
        lastKeyPressTimeRef.current = Date.now();
        // Clear existing flush timeout
        if (navigationFlushTimeoutRef.current) {
          clearTimeout(navigationFlushTimeoutRef.current);
          navigationFlushTimeoutRef.current = null;
        }
        // Schedule flush timeout (200ms after last keypress) to flush expensive state computation
        navigationFlushTimeoutRef.current = setTimeout(() => {
          // Flush pending debounced state computation when navigation stops
          debouncedSetStateEventIndex.flush();
          navigationFlushTimeoutRef.current = null;
        }, 200);
        // Call handler directly - selectedStep updates immediately, state computation is debounced
        handleStepBack();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        event.stopPropagation();
        isKeyboardNavRef.current = true;
        // Blur active element when panel is open to prevent focus ring from moving, which causes laggy scrolling
        if (isVisible && isFocusInPanel && activeElement instanceof HTMLElement) {
          activeElement.blur();
        }
        // Track keypress time for flush mechanism
        lastKeyPressTimeRef.current = Date.now();
        // Clear existing flush timeout
        if (navigationFlushTimeoutRef.current) {
          clearTimeout(navigationFlushTimeoutRef.current);
          navigationFlushTimeoutRef.current = null;
        }
        // Schedule flush timeout (200ms after last keypress) to flush expensive state computation
        navigationFlushTimeoutRef.current = setTimeout(() => {
          // Flush pending debounced state computation when navigation stops
          debouncedSetStateEventIndex.flush();
          navigationFlushTimeoutRef.current = null;
        }, 200);
        // Call handler directly - selectedStep updates immediately, state computation is debounced
        handleStepForward();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      // Cleanup flush timeout on unmount
      if (navigationFlushTimeoutRef.current) {
        clearTimeout(navigationFlushTimeoutRef.current);
        navigationFlushTimeoutRef.current = null;
      }
    };
  }, [
    isVisible,
    isLive,
    handleStepBack,
    handleStepForward,
    debouncedSetStateEventIndex,
    isReadOnly,
  ]);

  const panelSpring = useSpring({
    to: {
      width: isExpanded
        ? windowWidth >= 1024
          ? '35vw'
          : '40vw'
        : windowWidth >= 1024
          ? '15vw'
          : '20vw',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateX(0%)' : 'translateX(-100%)',
    },
    config: { tension: 280, friction: 26 },
  });

  const leftButtonsHidden = isVisible || leftPanelOpen;
  const bubbleSpring = useSpring({
    to: {
      opacity: leftButtonsHidden ? 0 : 1,
      transform: leftButtonsHidden ? 'translateX(-100%)' : 'translateX(0%)',
    },
    config: { tension: 300, friction: 28 },
  });

  const toggleExpand = () => setIsExpanded(!isExpanded);
  const toggleVisibility = () => setIsVisibleInternal(!isVisible);

  // Handle button click - toggle history mode
  const handleButtonClick = () => {
    if (onToggle) {
      onToggle();
    } else {
      toggleVisibility();
    }
  };

  const panelPositionClass = layout === 'results' ? 'fixed' : 'absolute';
  const historyPanelTopInsetRem = isSpectatorMode
    ? PANEL_TOP_INSET_LEFT_SPECTATOR_REM
    : PANEL_TOP_INSET_LEFT_REM;
  const historyPanelFrameStyle =
    layout === 'results'
      ? RESULTS_LAYOUT_PANEL_VERTICAL
      : {
          top: `calc(${BAR_TOP_HEIGHT_PERCENT}% + ${historyPanelTopInsetRem}rem)`,
          bottom: `calc(${BAR_BOTTOM_HEIGHT_PERCENT}% + ${PANEL_BOTTOM_INSET_REM}rem)`,
        };

  const historyFabBoardStyle: React.CSSProperties = {
    bottom: `calc(${BAR_BOTTOM_HEIGHT_PERCENT}% + ${FAB_BOTTOM_INSET_REM}rem)`,
    left: `${FAB_EDGE_INSET_REM}rem`,
  };
  const historyFabResultsWrapperClass = cn(
    'fixed z-50 pointer-events-auto left-3',
    RESULTS_LAYOUT_FAB_VERTICAL_CLASS,
  );

  if (!isVisible) {
    // If in history mode (not live), show expanded vertical pill FAB — interactive board only
    if (!isReadOnly && !isLive) {
      const pill = (
        <animated.div
          style={{
            ...bubbleSpring,
            ...(layout === 'results' ? {} : historyFabBoardStyle),
            pointerEvents: leftPanelOpen ? 'none' : 'auto',
          }}
          className={layout === 'results' ? undefined : 'fixed z-50'}
        >
          <div
            className={cn(
              'relative flex h-9 min-w-0 flex-row items-stretch overflow-hidden rounded-full border-2 border-amber-400 bg-amber-500/90 shadow-lg backdrop-blur-md md:h-10',
              'z-0',
              // Animated rotating ring border - amber-tinted conic gradient
              'before:absolute before:-top-1/2 before:-left-1/2 before:-z-2 before:h-[200%] before:w-[200%] before:rounded-full',
              'before:bg-[conic-gradient(from_0deg,#f59e0b,#fbbf24,#fef3c7,#ffffff,#fef3c7,#fbbf24,#f59e0b)]',
              'before:animate-[spin_3s_linear_infinite]',
              // Mask to show only border - thinner border with opaque background
              'after:absolute after:top-[2px] after:left-[2px] after:-z-1 after:h-[calc(100%-4px)] after:w-[calc(100%-4px)] after:rounded-full after:bg-amber-500',
            )}
          >
            {/* History icon and label - clickable to open panel, horizontal row */}
            <button
              onClick={handleButtonClick}
              className="flex flex-1 flex-row items-center gap-2 py-2 pr-2 pl-3 text-amber-900 transition-colors hover:bg-amber-400/50 md:pr-2 md:pl-4"
              aria-label={t('history_indicator')}
            >
              <CounterClockwiseClockIcon className="h-4 w-4 shrink-0 md:h-5 md:w-5" />
              <span className="text-sm font-semibold whitespace-nowrap">History Mode</span>
            </button>
            {/* Vertical divider */}
            <div className="w-px shrink-0 self-stretch bg-amber-400/50" />
            {/* Play icon - exit history mode/go live */}
            <Tooltip.Provider>
              <Tooltip.Root delayDuration={0}>
                <Tooltip.Trigger asChild>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onExitHistoryMode) {
                        onExitHistoryMode();
                      }
                    }}
                    className="flex h-full shrink-0 items-center justify-center px-2 text-amber-900 transition-colors hover:bg-amber-400/50 md:px-2.5"
                    aria-label="Exit history mode"
                  >
                    <PlayIcon className="h-4 w-4 md:h-5 md:w-5" />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    sideOffset={8}
                    className="rounded-lg border border-gray-100 bg-white px-2 py-2 text-sm shadow-xl"
                  >
                    {t('history_returnToLive')}
                    <Tooltip.Arrow className="fill-white" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          </div>
        </animated.div>
      );

      return layout === 'results' ? (
        <div className={cn(historyFabResultsWrapperClass, leftPanelOpen && 'pointer-events-none')}>
          {pill}
        </div>
      ) : (
        pill
      );
    }

    // Normal circular FAB when not in history mode (bottom-left, same size as zoom control)
    const tooltipOffset = 6;
    const circleFab = (
      <Tooltip.Provider delayDuration={0}>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <animated.button
              onClick={handleButtonClick}
              style={{
                ...bubbleSpring,
                ...(layout === 'results' ? {} : historyFabBoardStyle),
                pointerEvents: leftPanelOpen ? 'none' : 'auto',
              }}
              className={cn(
                'z-50 flex h-9 w-9 items-center justify-center rounded-full bg-amber-500 text-black shadow-lg md:h-10 md:w-10',
                layout === 'results' ? undefined : 'fixed',
              )}
              aria-label={t('history_indicator')}
            >
              <CounterClockwiseClockIcon className="h-4 w-4 md:h-5 md:w-5" />
            </animated.button>
          </Tooltip.Trigger>
          <Tooltip.Content
            side="right"
            sideOffset={tooltipOffset}
            className="rounded-lg border bg-white px-2 py-1 text-sm shadow-lg"
          >
            {t('history_indicator')}
          </Tooltip.Content>
        </Tooltip.Root>
      </Tooltip.Provider>
    );

    return layout === 'results' ? (
      <div className={cn(historyFabResultsWrapperClass, leftPanelOpen && 'pointer-events-none')}>
        {circleFab}
      </div>
    ) : (
      circleFab
    );
  }

  return (
    <>
      <animated.div
        ref={panelRef}
        style={{
          ...panelSpring,
          ...historyPanelFrameStyle,
        }}
        className={cn(
          'pointer-events-auto left-0 z-50 rounded-tr-lg rounded-br-lg border-2 border-amber-500/40 bg-white/90 p-2 select-text',
          panelPositionClass,
          'will-change-[width,transform]', // Optimize for smoother expand/collapse animations
        )}
      >
        <div className="flex h-full flex-col">
          {/* Control buttons on the right edge (mirrored from Chat's left-edge buttons) */}
          <div className="absolute top-1/2 right-0 z-10 translate-x-[50%] -translate-y-1/2 transform">
            <button
              onClick={toggleExpand}
              className="mb-2 block rounded-r-md bg-amber-500 p-2 text-black"
              aria-label={isExpanded ? t('history_panel_collapse') : t('history_panel_expand')}
            >
              {isExpanded ? (
                <ChevronLeftIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={() => {
                if (onToggle) {
                  onToggle();
                } else {
                  toggleVisibility();
                }
              }}
              className="block rounded-r-md bg-amber-500 p-2 text-black"
              aria-label="Close"
            >
              <Cross1Icon className="h-4 w-4" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <HistoryTimeline
              eventLog={
                eventLog as unknown as Array<{
                  eventType: string;
                  timestamp: number | Date;
                  payload?: unknown;
                  senderId?: string;
                }>
              }
              anchorState={anchorState}
              players={players}
              selectedEventIndex={selectedEventIndex}
              onSelectedEventIndexChange={onSelectedEventIndexChange}
              liveButton={isReadOnly ? 'none' : 'live'}
              viewMode="semantic"
              isLiveMode={isLive}
              isExpanded={isExpanded}
            />
          </div>
        </div>
      </animated.div>
    </>
  );
};

export default HistoryPanel;
